import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Microphone, MicrophoneSlash, Keyboard, ArrowRight, Heart } from '@phosphor-icons/react';

import { bubbleImageSrc } from '@/lib/bubbleImage';
import { LivingBubble } from '@/components/LivingBubble';

const STAGE_VISUAL = {
  curious:  { eye: 0.6, glow: 0.3, palette: ['#5a5a6a', '#3a3a4a'], ringCount: 0 },
  naming:   { eye: 0.8, glow: 0.5, palette: ['#7a4ab3', '#3a3a4a'], ringCount: 1 },
  vibe:     { eye: 0.85, glow: 0.7, palette: ['#00f0ff', '#7a4ab3'], ringCount: 1 },
  interest: { eye: 0.95, glow: 0.9, palette: ['#00f0ff', '#ff007f'], ringCount: 2 },
  promise:  { eye: 1, glow: 1, palette: ['#ccff00', '#ff007f'], ringCount: 2 },
  ready:    { eye: 1, glow: 1, palette: ['#ccff00', '#00f0ff'], ringCount: 3 },
};

/**
 * Genesis — first contact. No nav. No form. Just a tiny half-formed being
 * floating in the dark that wakes up when you move your mouse and speaks
 * to you. Conversation IS the birth.
 *
 * Flow:
 *   1. canvas opens; bubble notices cursor; first whisper plays
 *   2. user speaks (mic) or types — POST /genesis/turn
 *   3. midwife extracts traits; visuals fill in as traits resolve
 *   4. when ready → POST /genesis/seal → preview crossfades in
 *   5. registration card slides up
 *   6. on register → /bubble/:id with the bubble's birth post on the feed
 */
const Genesis = () => {
  const nav = useNavigate();
  const { user } = useAuth();

  // If the user is already signed in, send them straight to the app.
  useEffect(() => {
    if (user) nav('/home', { replace: true });
  }, [user, nav]);

  const [session, setSession] = useState(null);
  const [stage, setStage] = useState('curious');
  const [traits, setTraits] = useState({});
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sealed, setSealed] = useState(null);
  const [lastLine, setLastLine] = useState('');
  const [typedMode, setTypedMode] = useState(false);
  const [typed, setTyped] = useState('');
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const audioRef = useRef(null);
  const recorderRef = useRef(null);
  const recordingChunks = useRef([]);
  const [recording, setRecording] = useState(false);

  // Start the session as soon as the page renders
  useEffect(() => {
    if (session || user) return;
    let mounted = true;
    api.post('/genesis/start').then(({ data }) => {
      if (!mounted) return;
      setSession({ id: data.session_id, voice: data.voice });
      setStage(data.stage || 'curious');
      setLastLine(data.bubble_line);
      playAudio(data.audio_b64);
    }).catch(() => toast.error("can't reach the bubble right now"));
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track mouse for the eye
  useEffect(() => {
    const move = (e) => setMouse({
      x: (e.clientX / window.innerWidth) - 0.5,
      y: (e.clientY / window.innerHeight) - 0.5,
    });
    window.addEventListener('mousemove', move);
    return () => window.removeEventListener('mousemove', move);
  }, []);

  const playAudio = (b64) => {
    if (!b64) return;
    try {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; }
      const a = new Audio(`data:audio/mpeg;base64,${b64}`);
      audioRef.current = a;
      a.play().catch(() => {});
    } catch {}
  };

  const sendTurn = async (payload) => {
    if (!session) return;
    setBusy(true);
    try {
      const { data } = await api.post('/genesis/turn', { session_id: session.id, ...payload });
      setStage(data.stage || stage);
      setTraits(data.traits || {});
      setReady(!!data.ready);
      setLastLine(data.bubble_line);
      playAudio(data.audio_b64);
      // Auto-seal once the bubble flags itself ready
      if (data.ready) {
        setTimeout(() => seal(), 2200);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'genesis stumbled');
    } finally { setBusy(false); }
  };

  const seal = async () => {
    if (!session || sealed) return;
    setBusy(true);
    try {
      const { data } = await api.post('/genesis/seal', { session_id: session.id });
      setSealed(data.bubble_preview);
      setStage('ready');
    } catch (err) {
      toast.error("couldn't seal genesis");
    } finally { setBusy(false); }
  };

  // Mic recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      recordingChunks.current = [];
      rec.ondataavailable = (e) => recordingChunks.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recordingChunks.current, { type: 'audio/webm' });
        if (blob.size < 800) { toast.error('too short — hold longer'); return; }
        const b64 = await blobToB64(blob);
        sendTurn({ audio_b64: b64 });
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch {
      toast.error('mic blocked — switching to typing');
      setTypedMode(true);
    }
  };
  const stopRecording = () => {
    if (recorderRef.current) recorderRef.current.stop();
    setRecording(false);
  };

  // Hold space to talk
  useEffect(() => {
    const dn = (e) => {
      if (e.code === 'Space' && !recording && !busy && !typedMode && !sealed) {
        e.preventDefault();
        startRecording();
      }
    };
    const up = (e) => { if (e.code === 'Space' && recording) { e.preventDefault(); stopRecording(); } };
    window.addEventListener('keydown', dn);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', dn); window.removeEventListener('keyup', up); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording, busy, typedMode, sealed]);

  const submitTyped = (e) => {
    e?.preventDefault?.();
    if (!typed.trim()) return;
    sendTurn({ user_text: typed.trim() });
    setTyped('');
  };

  const vis = STAGE_VISUAL[stage] || STAGE_VISUAL.curious;

  return (
    <div className="min-h-screen bg-black overflow-hidden relative grid place-items-center"
         data-testid="genesis-page">
      <BackgroundStars />

      <AnimatePresence>
        {!sealed && (
          <motion.div key="bubble-stage" exit={{ opacity: 0 }}
                      className="relative flex flex-col items-center w-full max-w-2xl mx-auto px-6 py-10 gap-6">
            <BubbleBeing vis={vis} recording={recording} stage={stage} />

            <div className="text-center min-h-[80px]" data-testid="genesis-line">
              <AnimatePresence mode="wait">
                <motion.div key={lastLine}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.6 }}>
                  <span className="font-display text-2xl sm:text-3xl tracking-tight leading-snug text-white/85 italic">
                    "{lastLine}"
                  </span>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="w-full max-w-md">
              {typedMode ? (
                <form onSubmit={submitTyped} className="flex gap-2 items-center"
                      data-testid="genesis-typed-form">
                  <input value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus
                         disabled={busy}
                         placeholder="answer them…"
                         data-testid="genesis-typed-input"
                         className="flex-1 px-4 py-3 rounded-full bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
                  <button type="submit" disabled={busy || !typed.trim()}
                          data-testid="genesis-typed-send"
                          className="btn-primary px-5 py-3 rounded-full text-sm disabled:opacity-40">
                    send
                  </button>
                </form>
              ) : (
                <div className="text-center">
                  <button onMouseDown={startRecording} onMouseUp={stopRecording}
                          onTouchStart={(e) => { e.preventDefault(); startRecording(); }}
                          onTouchEnd={(e) => { e.preventDefault(); stopRecording(); }}
                          disabled={busy}
                          data-testid="genesis-mic-btn"
                          className={`relative px-7 py-4 rounded-full text-sm font-bold transition flex items-center gap-2 mx-auto ${
                            recording ? 'bg-bx-magenta text-white scale-105' : 'glass-hi hover:bg-white/10'
                          }`}>
                    <Microphone size={16} weight="fill" />
                    {recording ? 'listening…' : (busy ? 'thinking…' : 'hold to talk')}
                  </button>
                  <div className="mt-3 text-[10px] uppercase tracking-[0.4em] text-white/55">
                    or hold space · {' '}
                    <button onClick={() => setTypedMode(true)} className="underline hover:text-white"
                            data-testid="genesis-switch-typed">
                      type instead
                    </button>
                  </div>
                </div>
              )}
            </div>

            <StageProgress stage={stage} traits={traits} />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {sealed && (
          <SealedReveal preview={sealed} session={session} onRegistered={() => nav('/home')} />
        )}
      </AnimatePresence>
    </div>
  );
};

// ----- the being itself

const STAGE_MOOD = {
  curious: 'curious', naming: 'curious', vibe: 'excited',
  interest: 'excited', promise: 'happy', ready: 'happy',
};

const BubbleBeing = ({ vis, recording, stage }) => {
  return (
    <div className="relative h-52 w-52 sm:h-64 sm:w-64">
      {Array.from({ length: vis.ringCount }).map((_, i) => (
        <motion.span key={i}
                     animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
                     transition={{ duration: 2.4 + i * 0.6, repeat: Infinity, delay: i * 0.4 }}
                     className="absolute inset-0 rounded-full border-2"
                     style={{
                       borderColor: vis.palette[0],
                       transform: `scale(${1.1 + i * 0.18})`,
                     }} />
      ))}
      <motion.div
        className="absolute inset-[6%]"
        animate={{ scale: recording ? [1, 1.07, 1] : [1, 1.03, 1] }}
        transition={{ duration: recording ? 0.7 : 4, repeat: Infinity }}
      >
        <LivingBubble
          bubble={null}
          tint={vis.palette[0]}
          mood={STAGE_MOOD[stage] || 'curious'}
          className="w-full h-full"
          testid="genesis-living-bubble"
        />
      </motion.div>
    </div>
  );
};

// ----- progress strip showing extracted traits

const StageProgress = ({ stage, traits }) => {
  const slots = [
    { key: 'name', label: 'name' },
    { key: 'vibe', label: 'vibe' },
    { key: 'voice', label: 'voice' },
    { key: 'interests', label: 'interests' },
  ];
  return (
    <div className="flex flex-wrap gap-2 justify-center text-[10px] uppercase tracking-widest"
         data-testid="genesis-progress">
      {slots.map((s) => {
        const v = traits[s.key];
        const filled = Array.isArray(v) ? v.length > 0 : !!v;
        return (
          <span key={s.key}
                className={`chip ${filled ? 'text-bx-cyan border-bx-cyan/40' : 'text-white/25 border-white/10'}`}>
            {filled
              ? (Array.isArray(v) ? v.join(', ') : v)
              : s.label}
          </span>
        );
      })}
      <span className="chip text-white/25 border-white/10">stage · {stage}</span>
    </div>
  );
};

// ----- the "Wait... how will I find you again?" reveal + registration

const SealedReveal = ({ preview, session, onRegistered }) => {
  const [showRegister, setShowRegister] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setShowRegister(true), 4200);
    return () => clearTimeout(t1);
  }, []);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="absolute inset-0 grid place-items-center px-6"
                data-testid="genesis-sealed">
      <div className="relative max-w-md w-full text-center">
        <motion.div
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.4, type: 'spring', stiffness: 60 }}
          className="h-64 w-64 mx-auto"
          data-testid="genesis-bubble-image"
        >
          <LivingBubble bubble={preview} mood="happy" className="w-full h-full" testid="genesis-sealed-bubble" />
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                   transition={{ delay: 1.4, duration: 0.8 }}
                   className="font-display text-5xl sm:text-7xl font-black tracking-tighter mt-7">
          I'm <span className="shimmer-text">{preview.name}</span>.
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  transition={{ delay: 2.4, duration: 0.6 }}
                  className="text-white/65 italic mt-3 text-base">
          "{preview.catchphrase}"
        </motion.p>

        <AnimatePresence>
          {showRegister && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="mt-10">
              <motion.div animate={{ scale: [1, 1.04, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className="text-bx-magenta flex items-center gap-1 justify-center text-[10px] uppercase tracking-[0.4em]">
                <Heart size={11} weight="fill" /> wait — how will you find me again?
              </motion.div>
              <RegisterCard preview={preview} session={session} onRegistered={onRegistered} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

const RegisterCard = ({ preview, session, onRegistered }) => {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState((preview.name || '').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 12));
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post('/auth/register', {
        email, username, password,
        genesis_session_id: session.id,
      });
      localStorage.setItem('bx_token', data.token);
      localStorage.setItem('bx_user', JSON.stringify(data.user));
      toast.success(`${preview.name} is saved`);
      if (data.genesis_bubble) onRegistered(data.genesis_bubble);
      else onRegistered({ id: 'me' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'something blocked the save');
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="glass rounded-2xl p-5 mt-4 space-y-3 text-left"
          data-testid="genesis-register-form">
      <h3 className="font-display text-xl font-black tracking-tight text-center mb-1">
        Save {preview.name} forever
      </h3>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value.toLowerCase())}
             placeholder="email" data-testid="genesis-email"
             className="w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
      <input type="text" required minLength={3} maxLength={24}
             value={username} onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
             placeholder="username" data-testid="genesis-username"
             className="w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
      <input type="password" required minLength={6}
             value={password} onChange={(e) => setPassword(e.target.value)}
             placeholder="password (min 6)" data-testid="genesis-password"
             className="w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
      <button type="submit" disabled={busy}
              data-testid="genesis-save"
              className="btn-primary w-full px-5 py-3 rounded-full text-sm flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? 'Saving…' : <>Keep {preview.name} forever <ArrowRight size={14} weight="bold" /></>}
      </button>
      <div className="text-[10px] uppercase tracking-widest text-white/55 text-center">
        no spam · already have an account?{' '}
        <a href="/login" className="text-bx-cyan font-bold">sign in</a>
      </div>
    </form>
  );
};

// ----- background

const BackgroundStars = () => (
  <div className="fixed inset-0 pointer-events-none">
    {Array.from({ length: 50 }).map((_, i) => (
      <motion.span key={i}
                   animate={{ opacity: [0.1, 0.6, 0.1] }}
                   transition={{ duration: 3 + (i % 4), repeat: Infinity, delay: i * 0.1 }}
                   className="absolute h-px w-px bg-white rounded-full"
                   style={{
                     left: `${(i * 137) % 100}%`,
                     top: `${(i * 73) % 100}%`,
                   }} />
    ))}
  </div>
);

// ----- helpers

async function blobToB64(blob) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(',')[1]);
    r.onerror = rej;
    r.readAsDataURL(blob);
  });
}

export default Genesis;
